import type { BlockPriceComparison } from 'types/block-price-comparison';
import React from 'react';
import Image from 'next/image';
import { Wrap, Button, Logo } from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';

const PriceComparison: React.FC<
  BlockPriceComparison & { modified: string }
> = ({ sectionId, title, titleTag, intro, button, pricesTable, modified }) => {
  const { translations } = useWordPressContext();
  const date = new Date(modified.split('T')[0]);
  const dayString =
    date.getUTCDate() > 9 ? date.getUTCDate() : `0${date.getUTCDate()}`;
  const monthString =
    date.getUTCMonth() + 1 > 9
      ? date.getUTCMonth() + 1
      : `0${date.getUTCMonth() + 1}`;
  const dateString = `${monthString}/${dayString}/${date.getUTCFullYear()}`;
  const { leafwellCertification, leafwellRenewal, competitionTable } =
    pricesTable;

  return (
    <Wrap id={sectionId} className="xl:grid-cols-3 xl:gap-x-16 ">
      <div className="xl:col-span-1 ">
        <div className="grid gap-y-5">
          {title?.length > 0 ? (
            <HeadingBody
              level="2"
              tagName={titleTag as keyof JSX.IntrinsicElements}
              title={title}
            />
          ) : null}
          {intro?.length > 0 ? (
            <WysiwygContent
              content={intro}
              className={['max-w-xl'].join(' ')}
            />
          ) : null}
          {button ? (
            <div className="hidden xl:block">
              <Button
                href={button.url}
                label={button.title}
                rounded={true}
                target={button.target}
                variant="secondary"
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="xl:col-span-2">
        <div className="flex items-center mb-3 text-sm md:text-lg font-medium text-center">
          <div className="w-1/3 px-2 md:px-12"></div>
          <div className="w-1/3 px-2 md:px-12">First-time certification</div>
          <div className="w-1/3 px-2 md:px-12">Renewal certification</div>
        </div>
        <div className="flex items-center flex-row border border-x-0 md:border-x md:rounded-lg border-yellow-400 bg-yellow-400/10 text-2xl md:text-[43px] text-center">
          <div className="flex w-1/3 px-2 md:px-8 py-2 md:py-7">
            <Logo classNameButton="w-full" />
          </div>
          <div className="w-1/3 px-2 md:px-12 py-6 md:py-7">
            ${leafwellCertification}
          </div>
          <div className="relative w-1/3 px-2 md:px-12 py-6 md:py-7">
            <div className="flex items-center justify-center w-[60px] md:w-auto absolute top-0 bottom-0 left-0 -translate-x-2/4">
              <Image
                width={89}
                height={84}
                src="/best-value.svg"
                alt="Best care and best value"
              />
            </div>
            ${leafwellRenewal}
          </div>
        </div>
        {competitionTable.map(competitor => {
          return (
            <div
              className="flex border-b border-gray-300 md:text-[19px] text-center"
              key={competitor.name}
            >
              <div className="flex w-1/3 justify-start items-center px-2 md:px-12 py-5 md:py-6">
                {competitor.name}
              </div>
              <div className="flex w-1/3 justify-center items-center px-2 md:px-12 py-5 md:py-6 border-r border-gray-300">
                ${competitor.certification}
              </div>
              <div className="flex w-1/3 justify-center items-center px-2 md:px-12 py-5 md:py-6">
                ${competitor.renewal}
              </div>
            </div>
          );
        })}
        <p className="mt-4 pl-2 md:pl-12 text-xs font-medium">
          {translations['Retail price as of']} {dateString}
        </p>
      </div>
      <div className="block xl:hidden">
        {button ? (
          <Button
            href={button.url}
            label={button.title}
            rounded={true}
            target={button.target}
            variant="secondary"
          />
        ) : null}
      </div>
    </Wrap>
  );
};

export default PriceComparison;
